import React, { useMemo, useState } from 'react';
import { Modal, Text, Area, Select, EvidenceBadge, Banner } from './ui';
import { useStore, uid } from '../state/store';
import { ACTIVITY, EVIDENCE_LEVELS } from '../lib/constants';
import { classifyEvidenceText, validateEvidenceLevel } from '../lib/evidence';
import { orgMarkets } from '../lib/orgPlan';

export function classifyText(text) {
  return classifyEvidenceText(text);
}

export default function EvidenceForm({ onClose, defaults = {}, onSaved }) {
  const { state, add, today } = useStore();
  const markets = orgMarkets(state).map((m) => m.label);
  const [form, setForm] = useState({
    date: defaults.date || today,
    market: defaults.market || markets[0] || '',
    level: defaults.level || '',
    title: defaults.title || '',
    counterparty: defaults.counterparty || '',
    description: defaults.description || '',
    linkedTo: defaults.linkedTo || '',
  });

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const suggestion = useMemo(() => classifyText(`${form.title} ${form.description}`), [form.title, form.description]);
  const validation = useMemo(
    () => validateEvidenceLevel({ ...form, level: form.level || suggestion || '' }),
    [form, suggestion],
  );
  const level = validation.level || form.level || suggestion || 'E0';
  const isProof = level !== 'ACT' && level !== 'E0';

  const save = () => {
    const rec = {
      id: uid('ev'),
      ...form,
      level: validation.level,
      validationOk: validation.ok,
      validationReason: validation.reason || undefined,
    };
    add('evidence', rec);
    if (onSaved) onSaved(rec);
    onClose();
  };

  const canSave = !!String(form.title).trim() && !!validation.level;

  return (
    <Modal
      title="Record what happened"
      sub="Bridge45 classifies it. It does not accept flattering labels."
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" onClick={save} disabled={!canSave}>
            Record{validation.normalized || (!validation.ok && validation.level) ? ` as ${validation.level}` : ''}
          </button>
        </>
      }
    >
      <Text
        label="What happened"
        value={form.title}
        onChange={set('title')}
        placeholder="e.g. Developer X shared the asset data room"
      />

      {suggestion && !form.level && (
        <Banner tone={suggestion === 'ACT' || !validation.ok ? 'warn' : ''}>
          <div className="row tight">
            <span>Classified as</span>
            <EvidenceBadge level={suggestion} showName />
          </div>
          <div className="tiny" style={{ marginTop: 4 }}>
            {validation.reason
              ? validation.reason
              : suggestion === 'ACT'
                ? 'This is motion, not proof. It will not count toward E4 or E5.'
                : suggestion === 'E1'
                  ? 'Leading indicator only. This is not traction.'
                  : 'Override below if this is wrong — but do not promote it to look better.'}
          </div>
        </Banner>
      )}

      {form.level && !validation.ok && validation.reason && (
        <Banner tone="warn">
          <div className="row tight">
            <span>Will record as</span>
            <EvidenceBadge level={validation.level} showName />
          </div>
          <div className="tiny" style={{ marginTop: 4 }}>{validation.reason}</div>
        </Banner>
      )}

      <div className="grid g2" style={{ marginTop: 12 }}>
        <Select
          label="Evidence level"
          value={form.level || suggestion || ''}
          onChange={set('level')}
          options={[
            { value: '', label: suggestion ? `Use suggestion (${suggestion})` : 'Choose a level' },
            { value: ACTIVITY.id, label: 'Activity — motion, not proof' },
            ...EVIDENCE_LEVELS.map((e) => ({ value: e.id, label: `${e.id} — ${e.name}` })),
          ]}
        />
        <Select label="Market" value={form.market} onChange={set('market')} options={markets} />
      </div>

      <div className="grid g2">
        <Text label="Counterparty" value={form.counterparty} onChange={set('counterparty')} placeholder="Named organisation or person" />
        <Text label="Date" type="date" value={form.date} onChange={set('date')} />
      </div>

      <Area
        label="What exactly did they do?"
        value={form.description}
        onChange={set('description')}
        placeholder="Their action, not yours. What did the counterparty spend, share, commit or sign?"
      />

      {isProof && !String(form.counterparty).trim() && (
        <Banner tone="warn">Proof without a named counterparty is an assertion. Name who did it.</Banner>
      )}
    </Modal>
  );
}
